<template>
  <div class="filter-view-buttons">
    <v-menu v-if="isShowViewTypes" offset-y>
      <template #activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          value="list"
          :class="[
            { 'active-view-display': viewMode === 'list' },
            { 'px-0': $vuetify.breakpoint.smAndDown }
          ]"
          :x-small="$vuetify.breakpoint.smAndDown"
          v-on="on"
          @click="viewMode = 'list'"
        >
          <v-icon>{{ getBtnIcon }}</v-icon>
          <v-icon v-if="!$vuetify.breakpoint.xs" class="ml-1">
            mdi-menu-down
          </v-icon>
        </v-btn>
      </template>
      <v-list subheader>
        <v-list-item
          v-for="item in dropDownViewItems"
          :key="`${item.eventName}-${item.params}`"
          :value="item.params"
          @click="onItemClick(item)"
        >
          <v-list-item-icon class="mr-2">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import mixins from 'vue-typed-mixins';

import { SourceTypeEnum } from '@/bundles/App/enums';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { PIPELINE_FILTERS_QUERY_KEYS } from '@/bundles/Pipeline/helpers/dashboard/pipelineFiltersQuery';

interface IDropDownViewItem {
  eventName: string;
  params: string;
  icon: string;
  name: string;
  route?: string;
}

const dropDownViewItemsDictionary: Record<string, IDropDownViewItem[]> = {
  default: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View' },
    { eventName: 'setListViewEventHandler', params: 'cards', icon: 'mdi-card-text', name: 'Tile View' },
  ],
  availability: [
    { eventName: 'setListViewEventHandler', params: 'availabilityClient', icon: 'mdi-table-large', name: 'Standard View' },
    { eventName: 'setListViewEventHandler', params: 'availabilityWorkflow', icon: 'mdi-check-network', name: 'Workflow View' },
    { eventName: 'setListViewEventHandler', params: 'propertyType', icon: 'mdi-domain', name: 'Property Type View' },
    { eventName: 'setListViewEventHandler', params: 'cards', icon: 'mdi-card-text', name: 'Tile View' },
    { eventName: 'setListViewEventHandler', params: 'availabilityAdmin', icon: 'mdi-table-eye', name: 'Research View' },
    { eventName: 'goToAvailabilitiesDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'availabilities-dashboard' },
  ],
  [SourceTypeEnum.comparable]: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View' },
    { eventName: 'setListViewEventHandler', params: 'cards', icon: 'mdi-card-text', name: 'Tile View' },
    { eventName: 'goToComparablesDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'comparables-dashboard' },
  ],
  [SourceTypeEnum.pipeline]: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View' },
    { eventName: 'setListViewEventHandler', params: 'cards', icon: 'mdi-card-text', name: 'Tile View' },
    { eventName: 'goToPipelineDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'pipelines-dashboard' },
  ],
  [SourceTypeEnum.deal]: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View' },
    { eventName: 'setListViewEventHandler', params: 'cards', icon: 'mdi-card-text', name: 'Tile View' },
    { eventName: 'goToDealDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'deals-dashboard' },
  ],
  [SourceTypeEnum.pipelinesDashboard]: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View', route: 'pipelines' },
    { eventName: 'goToPipelineDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'pipelines-dashboard' },
  ],
  [SourceTypeEnum.dealsDashboard]: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View', route: 'deals' },
    { eventName: 'goToDealDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'deals-dashboard' },
  ],
  [SourceTypeEnum.availabilitiesDashboard]: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View', route: 'availabilities' },
    { eventName: 'goToAvailabilitiesDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'availabilities-dashboard' },
  ],
  [SourceTypeEnum.comparablesDashboard]: [
    { eventName: 'setListViewEventHandler', params: 'table', icon: 'mdi-table-large', name: 'List View', route: 'comparables' },
    { eventName: 'goToComparablesDashboard', params: 'dashboard', icon: 'mdi-view-dashboard', name: 'Dashboard View', route: 'comparables-dashboard' },
  ],
};

// Dashboards that scope themselves by a saved set through the shared `set_id` query key.
const SET_AWARE_DASHBOARD_ROUTES: string[] = [
  SourceTypeEnum.pipelinesDashboard,
  SourceTypeEnum.dealsDashboard,
  SourceTypeEnum.availabilitiesDashboard,
  SourceTypeEnum.comparablesDashboard,
];

export default mixins(AccountLayoutMixin).extend({
  name: 'FilterViewButton',

  props: {
    value: {
      type: String,
      default: 'list'
    },
    source: {
      type: String,
      default: ''
    },
    currentView: {
      type: String,
      default: ''
    },
    selectedSetId: {
      type: String,
      default: '',
    },
  },

  computed: {
    viewMode: {
      get: function () : string {
        return this.value
      },
      set: function (value : string) {
        this.$emit('input', value);
      }
    },
    isShowViewTypes: function () {
      const arr = ['availability', 'contact', 'company', 'occupier', 'property', 'comparable', SourceTypeEnum.pipeline, SourceTypeEnum.deal, 'project', 'requirement', 'marketingTemplates', 'customRows', SourceTypeEnum.pipelinesDashboard, SourceTypeEnum.dealsDashboard, SourceTypeEnum.availabilitiesDashboard, SourceTypeEnum.comparablesDashboard]

      return arr.some(item => item === this.source)
    },
    getBtnIcon: function () : string {
      switch (this.currentView) {
        case 'availabilityClient':
          return 'mdi-table-large';
        case 'availabilityAdmin':
          return 'mdi-table-eye';
        case 'availabilityWorkflow':
          return 'mdi-check-network';
        case 'dashboard':
          return 'mdi-view-dashboard'
        case 'propertyType':
          return 'mdi-domain';
        case 'dash':
        case 'cards':
          return 'mdi-card-text';
        default:
          return 'mdi-table-large';
      }
    },
    dropDownViewItems (): IDropDownViewItem[] {
      return dropDownViewItemsDictionary[this.source] || dropDownViewItemsDictionary.default;
    }
  },

  methods: {
    onItemClick (item: IDropDownViewItem) {
      if (item.route) {
        // Already on that view: navigating again would reset its filters, the applied set included, to the URL defaults.
        if (item.route === this.$route?.name) return;

        const query = SET_AWARE_DASHBOARD_ROUTES.includes(item.route) && this.selectedSetId
          ? { [PIPELINE_FILTERS_QUERY_KEYS.setId]: this.selectedSetId }
          : {};
        this.$router.push(this.getAccountRoute({ name: item.route, query }));
        return;
      }

      this.eventHandler({ eventName: item.eventName, params: item.params });
    },
    eventHandler: function ({ eventName, params } : { eventName: string, params: string}) {
      this.$emit('eventHandler', { eventName, params })
    }
  }
})
</script>
